/**
 * The captain: one person, derived from the run seed and nothing else.
 *
 * The gate this file exists to serve is memory. A playtester who lost a captain has to be
 * able to say that captain's NAME an hour later, unprompted. Everything below is chosen
 * against that bar rather than against completeness:
 *
 *  - **Real names, real surnames.** No syllable soup. Every given name and every surname
 *    here is a name a person actually has, drawn from ten cultural pools, and the pools
 *    match 65% of the time so most captains read as coherent people and the rest read as
 *    the mixed heritage that any crewed fleet actually contains. You cannot remember
 *    `Zar'thul Vexmoor`. You can remember Adaeze Okonkwo.
 *  - **An epithet on roughly one captain in five**, earned by temperament and given by a
 *    crew, not by a fantasy generator. `Halvard "Deadlight" Aalto` is a recall hook with a
 *    story attached, and the story is the temperament.
 *  - **A distinguishing mark on every captain, always.** Never "none". The face has to have
 *    one thing you would describe to someone else.
 *  - **Not every captain wears everything.** Hair, facial hair and headwear are optional
 *    layers with their own presence gene, drawn against age and pronoun set rather than
 *    folded into their own part tables as an index-0 "none". See `slots.ts`.
 *
 * **Determinism.** `captainSeed = (run_seed ^ fnv1a32('captain.<part>')) >>> 0`, the same
 * Appendix A.1 stream derivation `src/render/ship.ts` uses for the hull, applied to
 * presentation-only names that are deliberately NOT members of `STREAM_NAMES`. A captain
 * can never consume or perturb a gameplay stream, and a mid-run `vfx` draw can never change
 * who is flying. Paste a share string, get the same run and the same person. No
 * `Math.random`, no wall clock, no DOM.
 *
 * The identity draw and the appearance draw run on separate sub-streams, so adding an
 * appearance gene later cannot reshuffle anybody's name.
 *
 * **Permanence.** Nothing here writes to storage and nothing here reads from it. A captain
 * exists for exactly as long as the run does. That is the point; attachment is only worth
 * something if it can be spent.
 *
 * **Pronouns are a register, not a bio line.** `they` is drawn at exactly the same 1/3 as
 * `he` and `she`, and the dialogue layer keys lines on `voiceKey(captain)` =
 * `<temperament>:<pronoun>`. A they/them captain is not a he/him captain with words
 * swapped; the cadence, the self-reference and the line pool all differ downstream.
 *
 * This file declares no colours and no typefaces. Skin, hair, eye and uniform tints are
 * INDICES into ramps the portrait layer owns, so AMD-36's two-face rule and §11.14's
 * "no raw hex outside the palette module" are untouched by construction.
 */

import { RngStream, fnv1a32 } from '../core/rng';
import {
  BROW_POOLS,
  HAIR_POOLS,
  LAYERS,
  PRESENCE_STATES,
  presenceBit,
  presenceOdds,
  reachOf,
} from './slots';
import { drawTraits, type CaptainTraits } from './traits';

// ---------------------------------------------------------------------------
// Pronouns — the register selector
// ---------------------------------------------------------------------------

export const PRONOUN_IDS = ['she', 'he', 'they'] as const;
export type PronounId = (typeof PRONOUN_IDS)[number];

export interface PronounSet {
  id: PronounId;
  /** she / he / they */
  subject: string;
  /** her / him / them */
  object: string;
  /** her / his / their — the determiner form */
  possessive: string;
  /** hers / his / theirs — the standalone form */
  possessivePronoun: string;
  /** herself / himself / themself */
  reflexive: string;
  /** `they are`, `she is`. Drives verb agreement in every generated string. */
  plural: boolean;
}

export const PRONOUN_SETS: Readonly<Record<PronounId, PronounSet>> = {
  she: {
    id: 'she',
    subject: 'she',
    object: 'her',
    possessive: 'her',
    possessivePronoun: 'hers',
    reflexive: 'herself',
    plural: false,
  },
  he: {
    id: 'he',
    subject: 'he',
    object: 'him',
    possessive: 'his',
    possessivePronoun: 'his',
    reflexive: 'himself',
    plural: false,
  },
  they: {
    id: 'they',
    subject: 'they',
    object: 'them',
    possessive: 'their',
    possessivePronoun: 'theirs',
    reflexive: 'themself',
    plural: true,
  },
};

// ---------------------------------------------------------------------------
// Temperament — seven, each nameable from one line by a blind reader
// ---------------------------------------------------------------------------

export const TEMPERAMENT_IDS = [
  'drywit',
  'liturgist',
  'worrier',
  'veteran',
  'greenhorn',
  'clinician',
  'firebrand',
] as const;
export type TemperamentId = (typeof TEMPERAMENT_IDS)[number];

export interface Temperament {
  id: TemperamentId;
  /** What the crew would call this one, in a word. */
  label: string;
  /**
   * One line another agent can write voice from. This is the contract: if a written line
   * does not obey the definition, it belongs to a different captain.
   */
  definition: string;
  /** The observable speech behaviour a blind reader identifies the temperament by. */
  tell: string;
  /** Crew-given nicknames. Concrete, short, earned. */
  epithets: readonly string[];
  /** Age-band weights, index 0 youngest. A greenhorn with a grey beard breaks the fiction. */
  ageWeights: readonly number[];
  /**
   * Headwear indices this temperament reaches for when the affinity roll lands.
   *
   * Real shapes only. These used to include index 0 = "no hat", which meant a
   * temperament's taste in headgear and its odds of wearing any were the same number;
   * whether a captain is bare-headed now lives in `presenceOdds`.
   */
  headwearAffinity: readonly number[];
  /** Uniform indices this temperament reaches for when the affinity roll lands. */
  uniformAffinity: readonly number[];
}

export const TEMPERAMENTS: Readonly<Record<TemperamentId, Temperament>> = {
  drywit: {
    id: 'drywit',
    label: 'Dry',
    definition:
      'Underreacts on purpose: answers a catastrophe with a domestic detail, and lands the small word last.',
    tell: 'Short sentences. The joke is never signposted, and never about the joke.',
    epithets: ['Tallow', 'Sixpence', 'Ledger', 'Nickel', 'Weather', 'Molasses'],
    ageWeights: [0.14, 0.36, 0.34, 0.16],
    headwearAffinity: [0, 3, 7],
    uniformAffinity: [1, 3],
  },
  liturgist: {
    id: 'liturgist',
    label: 'Devout',
    definition:
      'Speaks in liturgy: frames the run as a rite owed to the dark, and the ship as something borrowed.',
    tell: 'Cadence and repetition. Says "we" where anybody else would say "I".',
    epithets: ['Candle', 'Vespers', 'Bellringer', 'Ash', 'Amen'],
    ageWeights: [0.1, 0.3, 0.38, 0.22],
    headwearAffinity: [2, 5, 7],
    uniformAffinity: [4, 6],
  },
  worrier: {
    id: 'worrier',
    label: 'Nervy',
    definition:
      'Thinks out loud and cannot stop: counts, checks, asks questions nobody answers, apologises for asking.',
    tell: 'Self-interruption. Numbers used as comfort. Sentences that trail rather than end.',
    epithets: ['Tallies', 'Barometer', 'Checklist', 'Knuckles', 'Thimble'],
    ageWeights: [0.34, 0.38, 0.21, 0.07],
    headwearAffinity: [1, 4, 5],
    uniformAffinity: [0, 2],
  },
  veteran: {
    id: 'veteran',
    label: 'Salt',
    definition:
      'Has done this before and lost people doing it: speaks in precedent, and warns by telling you what already happened to somebody else.',
    tell: 'Past tense creeping into present orders. Names of people who are not aboard.',
    epithets: ['Deadlight', 'Salvage', 'Old Tide', 'Ballast', 'Rust'],
    ageWeights: [0.01, 0.15, 0.46, 0.38],
    headwearAffinity: [0, 3, 6],
    uniformAffinity: [3, 5],
  },
  greenhorn: {
    id: 'greenhorn',
    label: 'Green',
    definition:
      'First command: performs competence with borrowed vocabulary, and cannot hide how happy this makes them.',
    tell: 'Formal in the wrong places, delighted in the wrong places. Quotes the manual.',
    epithets: ['Cadet', 'Fresh Paint', 'Manual', 'Firstlight', 'Sparrow'],
    ageWeights: [0.7, 0.24, 0.05, 0.01],
    headwearAffinity: [1, 4, 8],
    uniformAffinity: [0, 7],
  },
  clinician: {
    id: 'clinician',
    label: 'Cold',
    definition:
      'Withholds: states only what is operationally true, and lets the care leak out through the choice of what gets stated.',
    tell: 'No feeling words at all. Precision where warmth belongs.',
    epithets: ['Zero', 'Protocol', 'Scalpel', 'Northing', 'Quiet'],
    ageWeights: [0.12, 0.34, 0.36, 0.18],
    headwearAffinity: [2, 3, 5],
    uniformAffinity: [2, 4],
  },
  firebrand: {
    id: 'firebrand',
    label: 'Fire',
    definition:
      'Addresses the abyss directly: turns fear outward, into a dare said out loud to the thing in the dark.',
    tell: 'Second person aimed at the enemy, not at you. Talks past the player.',
    epithets: ['Hammerhead', 'Torch', 'Bad Weather', 'Firecracker', 'Knife'],
    ageWeights: [0.3, 0.4, 0.23, 0.07],
    headwearAffinity: [0, 1, 6],
    uniformAffinity: [1, 5],
  },
};

/**
 * The dialogue layer's stable key. A line pool is chosen by temperament AND pronoun set,
 * because the pronoun set is a register: same temperament, different cadence.
 */
export type VoiceKey = `${TemperamentId}:${PronounId}`;

// ---------------------------------------------------------------------------
// Name model
// ---------------------------------------------------------------------------

export const CULTURE_IDS = [
  'anglo',
  'slavic',
  'iberian',
  'westafrican',
  'levantine',
  'southasian',
  'eastasian',
  'nordic',
  'turkopersian',
  'pacific',
] as const;
export type CultureId = (typeof CULTURE_IDS)[number];

interface NamePool {
  /** Given names that read feminine in their culture of origin. */
  f: readonly string[];
  /** Given names that read masculine in their culture of origin. */
  m: readonly string[];
  /** Given names carried across the line in their culture of origin. */
  n: readonly string[];
  surnames: readonly string[];
}

/**
 * Ten pools of names people actually have. The `f` / `m` / `n` split is about how a name
 * reads in its own culture, not about who is allowed to carry it: the draw below lets any
 * pronoun set reach any pool, weighted so that most captains sound like someone you have
 * met and none of them sound like a random-syllable generator.
 */
const NAMES: Readonly<Record<CultureId, NamePool>> = {
  anglo: {
    f: [
      'Margaret',
      'Nell',
      'Bridget',
      'Iris',
      'Josephine',
      'Harriet',
      'Ruth',
      'Cora',
      'Edith',
      'Maeve',
      'Delia',
      'Prudence',
    ],
    m: [
      'Desmond',
      'Cormac',
      'Alistair',
      'Grady',
      'Wallace',
      'Ambrose',
      'Fletcher',
      'Hugh',
      'Silas',
      'Errol',
      'Barnaby',
      'Ronan',
    ],
    n: ['Rowan', 'Quinn', 'Marlow', 'Reese', 'Sloane', 'Frankie', 'Ellis', 'Emery', 'Blair', 'Lennox'],
    surnames: [
      'Ashcroft',
      'Doyle',
      'Merrick',
      'Halloran',
      'Whitlock',
      'Brennan',
      'Faraday',
      'Kirkwood',
      'Lambert',
      'Shaw',
      'Tremaine',
      'Vance',
    ],
  },
  slavic: {
    f: ['Milena', 'Katarina', 'Dragana', 'Zofia', 'Iryna', 'Vesna', 'Ludmila', 'Anezka', 'Bogna', 'Yelena'],
    m: [
      'Radomir',
      'Tomasz',
      'Bohdan',
      'Miroslav',
      'Kazimierz',
      'Dragan',
      'Vitaly',
      'Andrei',
      'Grigor',
      'Slavko',
    ],
    n: ['Sasha', 'Slava', 'Zhenya', 'Valya', 'Vanya', 'Nikita', 'Ilya', 'Mira'],
    surnames: [
      'Novak',
      'Zielinski',
      'Vukovic',
      'Hrabal',
      'Kowalczyk',
      'Marchenko',
      'Petrov',
      'Danilov',
      'Sokolov',
      'Jelinek',
      'Kruszewski',
      'Babic',
    ],
  },
  iberian: {
    f: ['Rosalia', 'Ines', 'Consuelo', 'Mireia', 'Paloma', 'Ximena', 'Beatriz', 'Dolores', 'Alba', 'Rocio'],
    m: ['Joaquin', 'Rodrigo', 'Emiliano', 'Nestor', 'Iker', 'Anselmo', 'Camilo', 'Rafael', 'Ignacio', 'Bruno'],
    n: ['Cruz', 'Guadalupe', 'Reyes', 'Trini', 'Yael', 'Alexis', 'Andrea', 'Amparo'],
    surnames: [
      'Quintana',
      'Vergara',
      'Beltran',
      'Ferreira',
      'Ocampo',
      'Salazar',
      'Mendoza',
      'Carvalho',
      'Rios',
      'Escobedo',
      'Peralta',
      'Aguirre',
    ],
  },
  westafrican: {
    f: ['Adaeze', 'Folasade', 'Amara', 'Nneka', 'Chidinma', 'Yaa', 'Abena', 'Zainab', 'Ifeoma', 'Temilade'],
    m: ['Kwabena', 'Emeka', 'Olumide', 'Chinedu', 'Kofi', 'Babatunde', 'Sekou', 'Obinna', 'Kwame', 'Adebayo'],
    n: ['Uche', 'Ndidi', 'Chidi', 'Ayo', 'Simi', 'Kamsi', 'Zuri', 'Ife', 'Sena', 'Ola'],
    surnames: [
      'Okonkwo',
      'Adeyemi',
      'Mensah',
      'Balogun',
      'Nwachukwu',
      'Owusu',
      'Diallo',
      'Achebe',
      'Osei',
      'Eze',
      'Sankoh',
      'Boateng',
    ],
  },
  levantine: {
    f: ['Yasmin', 'Layla', 'Nadia', 'Rania', 'Salma', 'Amira', 'Hind', 'Dalia', 'Zahra', 'Maysoun'],
    m: ['Tariq', 'Karim', 'Rashid', 'Anwar', 'Bilal', 'Hakim', 'Nizar', 'Faris', 'Idris', 'Samir'],
    n: ['Noor', 'Rida', 'Sabah', 'Ihsan', 'Amal', 'Wisam', 'Nihad', 'Rafah'],
    surnames: [
      'Haddad',
      'Nasser',
      'Khoury',
      'Farouk',
      'Mansour',
      'Bakri',
      'Sabbagh',
      'Qasim',
      'Halabi',
      'Sayegh',
      'Jaber',
      'Aboud',
    ],
  },
  southasian: {
    f: ['Meera', 'Anjali', 'Reva', 'Padma', 'Lakshmi', 'Nusrat', 'Kavya', 'Shalini', 'Devika', 'Sunita'],
    m: ['Arjun', 'Rajiv', 'Vikram', 'Ishaan', 'Pranav', 'Sanjay', 'Devendra', 'Imran', 'Tejas', 'Mahesh'],
    n: ['Kiran', 'Anmol', 'Jyoti', 'Shashi', 'Aman', 'Kabir', 'Nitya', 'Kirti'],
    surnames: [
      'Chatterjee',
      'Iyer',
      'Raghavan',
      'Bhatt',
      'Sandhu',
      'Kulkarni',
      'Nair',
      'Desai',
      'Qureshi',
      'Pillai',
      'Mukherjee',
      'Rathore',
    ],
  },
  eastasian: {
    f: ['Mei', 'Jia', 'Haruka', 'Yumi', 'Soojin', 'Lian', 'Xiulan', 'Nozomi', 'Areum', 'Hyejin'],
    m: ['Renji', 'Haruto', 'Jian', 'Minho', 'Kenzo', 'Tao', 'Hyun', 'Daichi', 'Zhen', 'Seungmin'],
    n: ['Yuki', 'Jing', 'Hikaru', 'Ren', 'Chun', 'Kaoru', 'Eun', 'Akira', 'Min'],
    surnames: [
      'Nakamura',
      'Ishida',
      'Zhao',
      'Lim',
      'Han',
      'Fujiwara',
      'Song',
      'Watanabe',
      'Cheng',
      'Bae',
      'Kurosawa',
      'Yoon',
    ],
  },
  nordic: {
    f: ['Sigrid', 'Astrid', 'Helle', 'Ingrid', 'Marit', 'Solveig', 'Birta', 'Aina', 'Eeva', 'Ronja'],
    m: ['Halvard', 'Torbjorn', 'Eirik', 'Sten', 'Kasper', 'Mikkel', 'Onni', 'Vidar', 'Leif', 'Anders'],
    n: ['Rune', 'Alva', 'Sol', 'Kim', 'Vega', 'Robin', 'Bo', 'Eli'],
    surnames: [
      'Halvorsen',
      'Lindqvist',
      'Aalto',
      'Bergstrom',
      'Nyman',
      'Vikander',
      'Sorensen',
      'Rask',
      'Kallio',
      'Holm',
      'Dahl',
      'Fossum',
    ],
  },
  turkopersian: {
    f: ['Nilufar', 'Gulnara', 'Sevda', 'Parisa', 'Shirin', 'Dilara', 'Aygul', 'Roya', 'Esma', 'Behnaz'],
    m: ['Behrouz', 'Kaveh', 'Emre', 'Bahram', 'Sinan', 'Orhan', 'Farhad', 'Cemal', 'Arash', 'Kerem'],
    n: ['Deniz', 'Evren', 'Yaren', 'Ilkay', 'Ozgur', 'Bahar', 'Ruzgar', 'Umut'],
    surnames: [
      'Demirci',
      'Yilmaz',
      'Kazemi',
      'Ozturk',
      'Farahani',
      'Aslan',
      'Shirazi',
      'Toprak',
      'Rahimi',
      'Erdem',
      'Bozkurt',
      'Ansari',
    ],
  },
  pacific: {
    f: ['Malia', 'Amihan', 'Dara', 'Thuy', 'Ratih', 'Noelani', 'Sinta', 'Mele', 'Anong', 'Ligaya'],
    m: ['Bayani', 'Tuan', 'Rizal', 'Made', 'Tevita', 'Danilo', 'Arif', 'Somchai', 'Ketut', 'Sione'],
    n: ['Kai', 'Chandra', 'Adi', 'Nala', 'Tama', 'Lani', 'Ari', 'Bayu'],
    surnames: [
      'Salvador',
      'Nguyen',
      'Tupou',
      'Wijaya',
      'Kahale',
      'Manalo',
      'Suharto',
      'Prasert',
      'Villaruz',
      'Bautista',
      'Iona',
      'Halim',
    ],
  },
};

/** How often the surname comes from the same pool as the given name. */
const SAME_CULTURE_P = 0.65;
/** How often a captain carries a crew-given epithet. */
const EPITHET_P = 0.22;

/**
 * Which given-name register a pronoun set reaches for, and how often. `they` is not a
 * fallback bucket: it draws hardest on the cross-the-line pool and still reaches the other
 * two half the time, because that is what the people it describes are actually called.
 */
const GIVEN_WEIGHTS: Readonly<Record<PronounId, { f: number; m: number; n: number }>> = {
  she: { f: 0.86, m: 0.0, n: 0.14 },
  he: { f: 0.0, m: 0.86, n: 0.14 },
  they: { f: 0.25, m: 0.25, n: 0.5 },
};

// ---------------------------------------------------------------------------
// Appearance genes
// ---------------------------------------------------------------------------

/**
 * Part-set sizes. Every field is an integer index the portrait layer resolves into a
 * drawn part; no colour is named here, only which ramp entry to use. Growing a set is
 * additive as long as the count grows at the END of the set, which keeps a shipped seed
 * drawing the same face.
 */
export const PART_COUNTS = {
  /** cranium 9 × temple 4 × ear 2 × jaw 3 × chin 3. Nine of these moved the jaw alone. */
  faceShape: 648,
  skinTone: 14,
  eyes: 11,
  brows: 9,
  nose: 9,
  mouth: 12,
  /** Twenty-four real styles. Index 0 used to be "shaved"; absence is `presence` now. */
  hair: 24,
  /** Eight real shapes. Index 0 used to be "none". */
  facialHair: 8,
  /** Nine real shapes. Index 0 used to be "none". */
  headwear: 9,
  uniform: 8,
  mark: 12,
  hairTint: 9,
  eyeTint: 7,
  uniformTint: 7,
  ageBand: 4,
  /** Where the mark sits, which way it faces and how far it runs. */
  markVar: 30,
  /** Gaze, brow lift, mouth pull and lid aperture. See `decodeAsymmetry` in `parts.ts`. */
  asymmetry: 216,
  /** How much hair this captain has, how far down it runs, and how the hat sits on it. */
  build: 27,
  /**
   * Which optional layers are painted. One bit each for hair, facial hair and headwear;
   * see `OPTIONAL` in `slots.ts`. Eight states, and every one of them is a captain you
   * could pick out of a line-up by silhouette alone.
   */
  presence: PRESENCE_STATES,
} as const;

export interface Appearance {
  /** 0..8 — jaw and cranium outline. */
  faceShape: number;
  /** 0..13 — ramp index, dark to light. Even across the ramp. */
  skinTone: number;
  /** 0..10 — eye shape and lid. */
  eyes: number;
  /** 0..8 — brow shape and weight. */
  brows: number;
  /** 0..8 — nose profile. */
  nose: number;
  /** 0..9 — mouth shape and resting set. */
  mouth: number;
  /**
   * 0..23 — hair style. Every index is a real style; whether the captain has hair at all
   * is `presence`, drawn against age and reach. Gendered: see `HAIR_TAGS`.
   */
  hair: number;
  /** 0..7 — beard shape. Untagged, so every shape stays reachable for every pronoun set. */
  facialHair: number;
  /** 0..8 — headwear shape. */
  headwear: number;
  /** 0..7 — collar, rig and rank flash. */
  uniform: number;
  /**
   * 0..11, never absent. Scar, burn, tattoo, ocular implant, missing ear, cauterised
   * socket, brand, freckling, vitiligo, chipped tooth, split brow, jaw plate. The one
   * thing a player would describe to someone else.
   */
  mark: number;
  /** 0..8 — ramp index for hair. */
  hairTint: number;
  /** 0..7 — ramp index for iris. */
  eyeTint: number;
  /** 0..6 — ramp index for uniform cloth. */
  uniformTint: number;
  /** 0..3, young to old. Weighted by temperament; drives line weight and hair tint. */
  ageBand: number;
  /** 0..29 — the mark's offset, side and length. Two captains never wear the same decal. */
  markVar: number;
  /**
   * 0..215 — the four unmirrored operations that stop a face reading as an identikit:
   * where the captain is looking, which brow is up, which mouth corner pulls, which eye
   * sits wider. All four stay inside slots the contract already lets break the mirror.
   */
  asymmetry: number;
  /**
   * 0..26 — hair body (thinner, as drawn, fuller) crossed with how far down the sides
   * run and how the hat is worn (pulled down, level, pushed back). All three change an
   * OUTLINE, which is the only cue that survives at `HUD_SCALE`, and none of them shows
   * up in a value histogram, which is why the first distinctness gate could not see the
   * problem it was built to catch.
   */
  build: number;
  /**
   * 0..7 — a bit per optional layer: hair, facial hair, headwear.
   *
   * The gene that ends "every slot always fires". Read it with `hasLayer`, never with a
   * literal mask: the bit order is `OPTIONAL`'s order and `OPTIONAL` is derived from
   * `LAYERS`, so a layer becoming optional later shifts nothing here by hand.
   */
  presence: number;
}

// ---------------------------------------------------------------------------
// Captain
// ---------------------------------------------------------------------------

export interface CaptainName {
  given: string;
  surname: string;
  /** Crew-given nickname, or null. Roughly one captain in five. */
  epithet: string | null;
  /** Which pool the given name came from. Portrait and dialogue may ignore this. */
  givenCulture: CultureId;
  /** Which pool the surname came from. Differs from `givenCulture` about a third of the time. */
  surnameCulture: CultureId;
  /** `Adaeze Okonkwo` or `Halvard "Deadlight" Aalto`. */
  full: string;
  /** What the HUD says when there is no room: `Okonkwo`, or the epithet if there is one. */
  short: string;
}

export interface Captain {
  /** The run seed this captain was derived from. */
  runSeed: number;
  name: CaptainName;
  pronouns: PronounSet;
  temperament: Temperament;
  appearance: Appearance;
  /**
   * One boon, one flaw, two flavour lines. The only part of a captain that touches the sim: both
   * traits land in `recomputeStats`' ADD buckets before the §4.2 caps. See `traits.ts` for the
   * pricing and for why the pair does not consult the temperament.
   */
  traits: CaptainTraits;
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

/**
 * `sub_seed = (run_seed ^ fnv1a32('captain.<part>')) >>> 0`. Appendix A.1's construction
 * applied to presentation-only names, exactly as `hullSeed` does. Two consequences that
 * matter: a captain cannot move a gameplay stream, and a new gene added to the appearance
 * draw cannot change anybody's name.
 */
export function captainSubSeed(runSeed: number, part: 'identity' | 'face' | 'traits'): number {
  return (runSeed ^ fnv1a32(`captain.${part}`)) >>> 0;
}

/** Index into `weights` proportionally. Weights need not sum to 1. */
function pickWeighted(rng: RngStream, weights: readonly number[]): number {
  let total = 0;
  for (let i = 0; i < weights.length; i++) total += weights[i];
  let r = rng.next() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r < 0) return i;
  }
  return weights.length - 1;
}

/**
 * The given-name register for a pronoun set. Falls through to the next non-empty bucket
 * rather than throwing, so a pool with an empty list can never produce an undefined name.
 */
function drawGivenRegister(rng: RngStream, pronoun: PronounId, pool: NamePool): readonly string[] {
  const w = GIVEN_WEIGHTS[pronoun];
  const order: (keyof typeof w)[] = ['f', 'm', 'n'];
  const weights = order.map((k) => (pool[k].length > 0 ? w[k] : 0));
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    for (const k of order) if (pool[k].length > 0) return pool[k];
  }
  return pool[order[pickWeighted(rng, weights)]];
}

function buildName(
  rng: RngStream,
  pronoun: PronounId,
  temperament: Temperament
): CaptainName {
  const givenCulture = rng.pick(CULTURE_IDS);
  const given = rng.pick(drawGivenRegister(rng, pronoun, NAMES[givenCulture]));

  const surnameCulture = rng.next() < SAME_CULTURE_P ? givenCulture : rng.pick(CULTURE_IDS);
  const surname = rng.pick(NAMES[surnameCulture].surnames);

  const hasEpithet = rng.next() < EPITHET_P;
  const epithet = hasEpithet ? rng.pick(temperament.epithets) : null;

  const full = epithet === null ? `${given} ${surname}` : `${given} "${epithet}" ${surname}`;
  const short = epithet ?? surname;

  return { given, surname, epithet, givenCulture, surnameCulture, full, short };
}

/** Draw weights for the seven iris values. Index 5 is warm, index 6 is clouded. */
const EYE_TINT_WEIGHTS: readonly number[] = [1, 1, 1, 1, 1, 0.18, 0.6];

function buildAppearance(rng: RngStream, pronoun: PronounId, temperament: Temperament): Appearance {
  const ageBand = pickWeighted(rng, temperament.ageWeights);
  const reach = reachOf(pronoun);

  const faceShape = rng.next_int(PART_COUNTS.faceShape);
  const skinTone = rng.next_int(PART_COUNTS.skinTone);
  const eyes = rng.next_int(PART_COUNTS.eyes);
  // Gendered pools. `rng.pick` over a frozen index list, so the draw is one call and the
  // pool is not rebuilt per captain. A `they` captain's pool is the whole table.
  const brows = rng.pick(BROW_POOLS[reach]);
  const nose = rng.next_int(PART_COUNTS.nose);
  const mouth = rng.next_int(PART_COUNTS.mouth);
  const hair = rng.pick(HAIR_POOLS[reach]);

  // Which, unconditionally — presence is drawn separately below. Keeping the two apart is
  // what lets a beard stay reachable for every pronoun set at a frequency that still reads
  // as a population: a she/her captain draws a beard shape every time and wears one 6 % of
  // the time, rather than being locked out of eight of the shapes.
  const facialHair = rng.next_int(PART_COUNTS.facialHair);

  // 45% of captains wear what their temperament reaches for; the rest wear whatever.
  const headwear =
    rng.next() < 0.45 ? rng.pick(temperament.headwearAffinity) : rng.next_int(PART_COUNTS.headwear);
  const uniform =
    rng.next() < 0.45 ? rng.pick(temperament.uniformAffinity) : rng.next_int(PART_COUNTS.uniform);

  // The optional layers, one Bernoulli each, in `LAYERS` order so the bit assignment is
  // the table's and not this function's.
  let presence = 0;
  for (const layer of LAYERS) {
    if (layer.required) continue;
    if (rng.next() < presenceOdds(layer.id, reach, ageBand, temperament.id, presence)) {
      presence |= presenceBit(layer.id);
    }
  }

  const mark = rng.next_int(PART_COUNTS.mark);
  const markVar = rng.next_int(PART_COUNTS.markVar);
  const asymmetry = rng.next_int(PART_COUNTS.asymmetry);
  const build = rng.next_int(PART_COUNTS.build);

  // Grey arrives with age but never becomes compulsory: the top two ramp entries are the
  // grey end, and an old captain reaches them 55% of the time rather than always.
  const greyP = [0.0, 0.04, 0.24, 0.55][ageBand];
  const hairTint =
    rng.next() < greyP
      ? PART_COUNTS.hairTint - 2 + rng.next_int(2)
      : rng.next_int(PART_COUNTS.hairTint - 2);

  // Iris 5 is the warm accent and iris 6 is the clouded, sightless eye. Uniform sampling
  // put gold eyes on one captain in eight, which stopped reading as an augmentation and
  // started reading as the house style. Weighted, gold is a 1-in-38 draw and stays the
  // thing a player mentions.
  const eyeTint = pickWeighted(rng, EYE_TINT_WEIGHTS);
  const uniformTint = rng.next_int(PART_COUNTS.uniformTint);

  return {
    faceShape,
    skinTone,
    eyes,
    brows,
    nose,
    mouth,
    hair,
    facialHair,
    headwear,
    uniform,
    mark,
    hairTint,
    eyeTint,
    uniformTint,
    ageBand,
    markVar,
    asymmetry,
    build,
    presence,
  };
}

/**
 * The whole person, from the run seed. Pure: same seed in, identical captain out, forever.
 * Call it once per run and hold the result; nothing here caches and nothing here persists.
 */
export function makeCaptain(runSeed: number): Captain {
  const seed = runSeed >>> 0;

  const idRng = new RngStream(captainSubSeed(seed, 'identity'));
  const pronouns = PRONOUN_SETS[PRONOUN_IDS[idRng.next_int(PRONOUN_IDS.length)]];
  const temperament = TEMPERAMENTS[TEMPERAMENT_IDS[idRng.next_int(TEMPERAMENT_IDS.length)]];
  const name = buildName(idRng, pronouns.id, temperament);

  const faceRng = new RngStream(captainSubSeed(seed, 'face'));
  const appearance = buildAppearance(faceRng, pronouns.id, temperament);

  // Third sub-stream, disjoint from the other two by construction, so the boon / flaw gene added
  // here reshuffled no name and no face. `tests/captain-traits.test.ts` pins that against
  // goldens captured before this line existed.
  const traitRng = new RngStream(captainSubSeed(seed, 'traits'));
  const traits = drawTraits(traitRng, temperament.id);

  return { runSeed: seed, name, pronouns, temperament, appearance, traits };
}

/** `<temperament>:<pronoun>` — the key the dialogue layer selects a line pool with. */
export function voiceKey(c: Captain): VoiceKey {
  return `${c.temperament.id}:${c.pronouns.id}`;
}

/**
 * How the captain is addressed in prose the game writes about them: `Captain Okonkwo`,
 * or `Captain "Deadlight" Aalto` where the crew got there first.
 */
export function addressed(c: Captain): string {
  return c.name.epithet === null
    ? `Captain ${c.name.surname}`
    : `Captain "${c.name.epithet}" ${c.name.surname}`;
}
